/**
 * Analyzer: Emotional Tilt (NEW)
 *
 * Detects behavioral signatures of emotional trading by looking at
 * patterns that cluster after losses:
 *   1. Revenge sizing — quantity increases after a loss
 *   2. Rapid re-entry — next trade too fast after a loss (< 3 minutes)
 *   3. Loss chain — 3+ consecutive losses without a pause
 *
 * These are the single most actionable signals for a discretionary
 * trader because tilt is reversible in-session if you catch it.
 *
 * MULTI-ACCOUNT GUARD:
 *   Prop-firm traders routinely fan a single decision across 3-7 accounts.
 *   Without collapsing, the analyzer sees one decision as 3 trades all at
 *   the same minute and confidently reports "3-loss streak + 2 rapid
 *   re-entries" — a phantom tilt diagnosis on a perfectly disciplined
 *   single entry. We collapse same-decision fan-outs (same symbol +
 *   direction + entryTime + entry price across DIFFERENT accountIds) to
 *   one representative event BEFORE running tilt detection. Same-account
 *   duplicates (which would be real over-trading) are NOT collapsed.
 *   See utils.collapseMultiAccountDecisions for the dedup semantics.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../types.js';
import { sortByEntryTime, minutesBetween, collapseMultiAccountDecisions } from '../utils.js';

export const key = 'emotionalTilt';

const REVENGE_SIZE_MULTIPLIER = 1.5;   // >=1.5x prior-trade size after a loss
const RAPID_REENTRY_MINUTES = 3;       // < 3 min between loss and next entry
const LOSS_CHAIN_MIN = 3;              // 3+ consecutive losses

export const analyze = (ctx) => {
  const { session, enriched } = ctx;
  if (!session || !Array.isArray(enriched) || enriched.length < 3) return null;

  // Collapse multi-account fan-outs to ONE decision each before applying
  // tilt logic — see the file header for why. After collapse we may have
  // fewer than 3 distinct decisions in this session; in that case there's
  // simply not enough behavioral data to draw a tilt conclusion.
  const collapsed = collapseMultiAccountDecisions(enriched);
  if (collapsed.length < 3) return null;

  const sorted = sortByEntryTime(collapsed);

  // Track the three signals
  let revengeCount = 0;
  let rapidReentryCount = 0;
  let longestLossChain = 0;
  let currentLossChain = 0;
  const examples = { revenge: [], rapidReentry: [], lossChain: 0 };

  for (let i = 0; i < sorted.length; i++) {
    const { trade, netPnl } = sorted[i];

    // Loss-chain tracking
    if (netPnl < 0) {
      currentLossChain++;
      if (currentLossChain > longestLossChain) longestLossChain = currentLossChain;
    } else if (netPnl > 0) {
      currentLossChain = 0;
    }

    // Look at transitions from loss → next trade
    if (i === 0) continue;
    const prev = sorted[i - 1];
    if (prev.netPnl >= 0) continue; // only interested in "after a loss"

    // 1) Revenge sizing
    const prevQty = parseInt(prev.trade.quantity) || 0;
    const curQty = parseInt(trade.quantity) || 0;
    if (prevQty > 0 && curQty >= prevQty * REVENGE_SIZE_MULTIPLIER) {
      revengeCount++;
      if (examples.revenge.length < 2) examples.revenge.push({ prevQty, curQty });
    }

    // 2) Rapid re-entry
    const gap = minutesBetween(prev.trade, trade);
    if (gap !== null && gap >= 0 && gap < RAPID_REENTRY_MINUTES) {
      rapidReentryCount++;
      if (examples.rapidReentry.length < 2) examples.rapidReentry.push({ gap });
    }
  }
  examples.lossChain = longestLossChain;

  // ── Signal classification (v7.6.2 — revenge as primary discriminator) ──
  //
  // Old logic: 2+ signals fired → CRITICAL regardless of which two. This
  // produced chronic false positives for traders running R&D-mode regimes
  // (deliberate small size + fast cadence + marginal trades). Rapid re-entry
  // and loss chains fire structurally in research cadence WITHOUT being tilt
  // — they're just samples being gathered efficiently.
  //
  // New logic: REVENGE SIZING is the discriminative signal because it's a
  // discretionary in-the-moment decision (the trader actively chose to
  // increase size after a loss). Rapid re-entry and loss chain alone
  // describe surface behavior that can occur with or without tilt. The
  // diagnostic value of the marker depends on whether revenge sizing is
  // present.
  //
  // Severity matrix:
  //   Revenge + ANY other signal           = CRITICAL (compounding bad
  //                                                     decisions)
  //   Revenge alone                        = WARNING
  //   2+ non-revenge signals (rapid/chain) = WARNING with "active cadence"
  //                                          framing — flagged but downgraded
  //   1 non-revenge signal                 = WARNING with same framing
  //   Loss chain ≥ 4 alone                 = WARNING (still worth flagging,
  //                                                    but not CRITICAL
  //                                                    without revenge)
  const signals = [
    { name: 'revenge',   count: revengeCount,     threshold: 1 },
    { name: 'rapid',     count: rapidReentryCount, threshold: 1 },
    { name: 'chain',     count: longestLossChain,  threshold: LOSS_CHAIN_MIN }
  ];
  const active = signals.filter(s => s.count >= s.threshold);
  if (active.length === 0) return null;

  const parts = [];
  if (revengeCount > 0) {
    parts.push(`${revengeCount} size jump${revengeCount > 1 ? 's' : ''} (${REVENGE_SIZE_MULTIPLIER}x+) right after a loss`);
  }
  if (rapidReentryCount > 0) {
    parts.push(`${rapidReentryCount} re-entry within ${RAPID_REENTRY_MINUTES} min of a loss`);
  }
  if (longestLossChain >= LOSS_CHAIN_MIN) {
    parts.push(`${longestLossChain}-loss streak without a pause`);
  }

  const hasRevenge = revengeCount > 0;
  const multi = active.length >= 2;

  // v7.6.2 — Research-mode awareness, per-account-corrected.
  //
  // Mode lives on each trade (`trade.mode`), inherited from `account.mode`
  // at trade creation time. The cap-check is intentionally conservative:
  // we only cap severity if EVERY trade in the session is research-mode.
  // If even one trade is from a production-mode or live-mode account,
  // we treat the session as production for tilt purposes — that prevents
  // cross-account pollution where a research trade on Account A could
  // accidentally downgrade legit tilt on Account B in the same session.
  //
  // A future refactor will split the analyzer to produce per-account
  // observations (one tilt diagnosis per account, capped independently
  // based on that account's mode). For now, the conservative session-wide
  // rule is the correctness fix: when in doubt, escalate normally.
  //
  // Legacy trades from before v7.6.2 have no `mode` field — they're
  // treated as non-research (the default 'production' fallback). That
  // keeps existing data behavior unchanged after the upgrade.
  const inResearchMode = sorted.length > 0
    && sorted.every(e => e.trade?.mode === 'research');

  // CRITICAL requires revenge sizing to be present. Without revenge, the
  // pattern describes surface cadence (which may or may not be tilt depending
  // on the trader's intent — researching marginal setups vs. emotional
  // reaction). The journal should surface it for the trader to check, but
  // shouldn't escalate to CRITICAL without the decisional shift that
  // revenge sizing represents.
  let severity;
  if (inResearchMode) {
    // Research mode cap: never CRITICAL. The R&D-cadence false-positive fix.
    severity = SEVERITY.WARNING;
  } else if (hasRevenge && multi) {
    severity = SEVERITY.CRITICAL;
  } else if (hasRevenge) {
    severity = SEVERITY.WARNING;
  } else {
    severity = SEVERITY.WARNING;
  }
  const confidence = hasRevenge ? (multi ? 0.88 : 0.78) : 0.62;
  const priority = hasRevenge ? (multi ? 90 : 78) : 60;

  // Framing differs by signal mix. Revenge-present = tilt language. Revenge-
  // absent = "active cadence" language — the marker is firing on the surface
  // pattern but the discretionary-decision signature is missing, so it might
  // be R&D cadence rather than tilt. Trader checks themselves.
  const title = hasRevenge
    ? (multi
        ? 'Multiple tilt markers fired this session'
        : `Tilt marker: ${parts[0]}`)
    : (multi
        ? 'Active cadence detected — check yourself'
        : `Active cadence: ${parts[0]}`);

  const action = hasRevenge
    ? (multi
        ? 'Hard stop next session: mandatory 10-min pause after any loss, size locked to baseline.'
        : 'Set a rule for this specific pattern — the most valuable guardrails are the ones matched to your actual leaks.')
    : (multi
        ? 'Your size held steady (no revenge sizing) — the surface pattern fired but the discretionary-decision marker is absent. If these trades were setup-driven, this is R&D cadence not tilt; if they were reaction-driven, this is worth flagging anyway.'
        : 'Surface pattern fired without size escalation. Worth a quick check: were these setup-driven (research cadence) or reaction-driven (early tilt forming)?');

  return makeObservation({
    id: `${key}:${session.id}:tilt`,
    analyzer: key,
    scope: 'session',
    scopeId: session.id,
    severity,
    category: CATEGORY.EMOTIONAL,
    title,
    detail: parts.join(' · '),
    action,
    evidence: {
      revengeCount, rapidReentryCount, longestLossChain, examples,
      // Surface the revenge-discriminator state so downstream consumers
      // (LLM narrator, future Coach Voice) can reference it correctly.
      hasRevengeSignal: hasRevenge,
      // v7.6.2 — research-mode flag exposed for downstream consumers (LLM
      // narrator can reference: "the session is research-tagged so this
      // pattern is expected by design").
      inResearchMode,
      thresholds: { size: REVENGE_SIZE_MULTIPLIER, reentryMin: RAPID_REENTRY_MINUTES, chainMin: LOSS_CHAIN_MIN }
    },
    confidence,
    priority
  });
};
